import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Tabs from '../components/common/Tab/Tabs';
import SearchArea from '../components/WeeklyReport/SearchArea/SearchArea';
import { Report } from '../components/WeeklyReport/SearchArea/WeeklyMasterProvider';
import Content from '../components/WeeklyReport/Content/Content';
import Footer from '../components/WeeklyReport/Footer/Footer';

// import { WebSocketDemo } from '../components/WebSocket/UseWebsocket'
import { useUser } from '../context/UserContext';
import { useUserProfile } from '../context/UserProfileContext';
import {
  ACCESS_CONTEXT_UPDATED_AT_KEY,
  ACCESS_CONTEXT_UPDATED_EVENT,
  isWeeklyDataCacheStale,
} from '../lib/accessContextUpdate';
import { WEEKLY_REPORT_UPDATED_AT_KEY } from '../components/WeeklyReport/SearchArea/SearchArea.constants';
import { buildWeeklyViewerAccessContextKey } from '../lib/weeklyViewerAccessContext';
import { subscribeWeeklyReportPublicScopeRefresh } from '../lib/weeklyReportPublicScopeRefresh';

const WEEKLY_REPORT_SESSION_RESULTS_CACHE_VERSION = 2;
const ENABLE_PERSISTED_WEEKLY_RESULT_CACHE = false;

type WeeklyReportSessionResultsCache = {
  version: number;
  savedAt: string;
  accessContextKey: string;
  results: Report[];
};

const readWeeklyReportUpdatedAtMs = (): number | null => {
  try {
    const raw = localStorage.getItem(WEEKLY_REPORT_UPDATED_AT_KEY);
    if (!raw) return null;
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const areSameReportListByReference = (a: Report[], b: Report[]): boolean =>
  a.length === b.length && a.every((item, idx) => item === b[idx]);

const WEEKLY_REPORT_URL_SEARCH_KEYS = [
  'wr_id',
  'start_date',
  'end_date',
  'free_word',
  'search_title_body',
  'search_tags',
  'report_types',
  'teams',
  'create_by',
  'departments',
  'temporary_flg',
  'create_by_none',
  'start_preset',
] as const;

const hasInitialWeeklyReportUrlSearchCondition = (): boolean => {
  if (typeof window === 'undefined') return false;

  const searches = [window.location.search];
  const hashQueryIndex = window.location.hash.indexOf('?');
  if (hashQueryIndex >= 0) {
    searches.push(window.location.hash.slice(hashQueryIndex));
  }

  return searches.some((search) => {
    if (!search) return false;
    const params = new URLSearchParams(search);
    return WEEKLY_REPORT_URL_SEARCH_KEYS.some((key) => params.has(key));
  });
};

const WeeklyReport: React.FC = () => {
  // Contextからuser取得
  const user = useUser();
  const { profile, loaded: profileLoaded } = useUserProfile();
  const viewerAccessContextKey = useMemo(
    () =>
      buildWeeklyViewerAccessContextKey({
        profileLoaded,
        department: profile?.department,
        teamId: profile?.preferredTeamId,
        updatedAt: profile?.preferredTeamUpdatedAt,
      }),
    [
      profileLoaded,
      profile?.department,
      profile?.preferredTeamId,
      profile?.preferredTeamUpdatedAt,
    ]
  );
  const isViewerAccessContextReady = viewerAccessContextKey !== null;
  const initialHasUrlSearchConditionRef = useRef(
    hasInitialWeeklyReportUrlSearchCondition()
  );

  // 検索結果を格納する state
  const [results, setResults] = useState<Report[]>([]);
  const [mailExportReports, setMailExportReports] = useState<Report[]>([]);
  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
  const mailExportReportsRef = useRef<Report[]>([]);

  const updateMailExportReports = useCallback((next: Report[]) => {
    if (!Array.isArray(next)) return;
    if (areSameReportListByReference(mailExportReportsRef.current, next)) return;
    mailExportReportsRef.current = next;
    setMailExportReports(next);
  }, []);

  // =========================================================
  // ここから「戻ってきて一度表示されたら消えない」対策
  // =========================================================

  // ログインID（キャッシュキー用）
  const selfLoginId = useMemo(() => {
    const raw =
      (user as any)?.signInDetails?.loginId ||
      (user as any)?.attributes?.email ||
      (user as any)?.username;
    return raw ? String(raw).trim() : 'anon';
  }, [user]);

  const RESULTS_CACHE_KEY = useMemo(
    () => `WeeklyReport:lastResults:${selfLoginId}`,
    [selfLoginId]
  );

  const readCache = useCallback((): Report[] | null => {
    if (!viewerAccessContextKey) return null;
    if (!ENABLE_PERSISTED_WEEKLY_RESULT_CACHE) {
      try {
        sessionStorage.removeItem(RESULTS_CACHE_KEY);
      } catch {}
      return null;
    }
    try {
      const raw = sessionStorage.getItem(RESULTS_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<WeeklyReportSessionResultsCache>;
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        parsed.version !== WEEKLY_REPORT_SESSION_RESULTS_CACHE_VERSION ||
        typeof parsed.savedAt !== 'string' ||
        parsed.accessContextKey !== viewerAccessContextKey ||
        !Array.isArray(parsed.results)
      ) {
        // 保存時刻を検証できない旧形式は、権限変更後の表示を防ぐため利用しない。
        sessionStorage.removeItem(RESULTS_CACHE_KEY);
        return null;
      }
      if (
        isWeeklyDataCacheStale(
          parsed.savedAt,
          readWeeklyReportUpdatedAtMs()
        )
      ) {
        sessionStorage.removeItem(RESULTS_CACHE_KEY);
        return null;
      }
      return parsed.results as Report[];
    } catch {
      return null;
    }
  }, [RESULTS_CACHE_KEY, viewerAccessContextKey]);

  const writeCache = useCallback(
    (next: Report[]) => {
      if (!viewerAccessContextKey) return;
      if (!ENABLE_PERSISTED_WEEKLY_RESULT_CACHE) return;
      try {
        const payload: WeeklyReportSessionResultsCache = {
          version: WEEKLY_REPORT_SESSION_RESULTS_CACHE_VERSION,
          savedAt: new Date().toISOString(),
          accessContextKey: viewerAccessContextKey,
          results: next,
        };
        sessionStorage.setItem(RESULTS_CACHE_KEY, JSON.stringify(payload));
      } catch {}
    },
    [RESULTS_CACHE_KEY, viewerAccessContextKey]
  );

  const deleteCache = useCallback(() => {
    try {
      sessionStorage.removeItem(RESULTS_CACHE_KEY);
    } catch {}
  }, [RESULTS_CACHE_KEY]);

  // ページを離れる（タブを閉じる・別ページへ遷移・戻る/進む等）
  // タイミングで localStorage のキャッシュを消す
  useEffect(() => {
    const handlePageHide = () => {
      deleteCache();
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      deleteCache();
    };
  }, [deleteCache]);

  // state の反映を待たずに「今の結果」を同期的に参照するための ref
  const resultsRef = useRef<Report[]>([]);
  const hasNonEmptyRef = useRef<boolean>(false);       // 「非0件を表示したことがある」事実
  const lastNonEmptyAtRef = useRef<number>(0);         // 直近の非0件受信時刻

  // 戻ってきた直後は onResults([]) を無視したい
  const keepResultsUntilUserSearchRef = useRef<boolean>(false);

  // SearchArea の onSearchPerformed が「ユーザー操作」か不明なので、
  //    “戻ってきた直後”は userSearch 扱いしない
  const mountedAtRef = useRef<number>(Date.now());
  const lastUserSearchAtRef = useRef<number | null>(null);

  // 0件クリアのチラつき回避（短い猶予）
  const emptyCommitTimerRef = useRef<number | null>(null);
  const clearEmptyCommitTimer = useCallback(() => {
    if (emptyCommitTimerRef.current != null) {
      window.clearTimeout(emptyCommitTimerRef.current);
      emptyCommitTimerRef.current = null;
    }
  }, []);

  // mount 時に sessionStorage から復元
  useEffect(() => {
    if (!isViewerAccessContextReady) return;
    mountedAtRef.current = Date.now();
    clearEmptyCommitTimer();

    if (initialHasUrlSearchConditionRef.current) {
      resultsRef.current = [];
      hasNonEmptyRef.current = false;
      lastNonEmptyAtRef.current = 0;
      keepResultsUntilUserSearchRef.current = false;
      lastUserSearchAtRef.current = null;
      deleteCache();
      return;
    }

    const cached = readCache();
    if (cached && cached.length > 0) {
      // state
      setResults(cached);
      setSearchPerformed(true);

      // ref（同期更新）
      resultsRef.current = cached;
      hasNonEmptyRef.current = true;
      lastNonEmptyAtRef.current = Date.now();

      // 戻り直後は空配列を無視する
      keepResultsUntilUserSearchRef.current = true;
      lastUserSearchAtRef.current = null;
    } else {
      resultsRef.current = [];
      hasNonEmptyRef.current = false;
      lastNonEmptyAtRef.current = 0;
      keepResultsUntilUserSearchRef.current = false;
      lastUserSearchAtRef.current = null;
    }

    return () => {
      clearEmptyCommitTimer();
    };
  }, [
    isViewerAccessContextReady,
    readCache,
    clearEmptyCommitTimer,
    deleteCache,
  ]);

  const clearAccessSensitiveResults = useCallback(() => {
    clearEmptyCommitTimer();
    resultsRef.current = [];
    hasNonEmptyRef.current = false;
    lastNonEmptyAtRef.current = 0;
    keepResultsUntilUserSearchRef.current = false;
    lastUserSearchAtRef.current = null;
    setResults([]);
    updateMailExportReports([]);
    deleteCache();
  }, [clearEmptyCommitTimer, deleteCache, updateMailExportReports]);

  useEffect(
    () =>
      subscribeWeeklyReportPublicScopeRefresh((reason) => {
        // focus / visibilitychange は SearchArea 側で公開範囲だけを
        // バックグラウンド再検証する。画面復帰だけで一覧を空にしない。
        if (reason === 'focus' || reason === 'visibility') return;

        // マスタ管理で公開範囲が実際に更新された通知は、
        // 古い権限の結果を残さないため即時破棄する。
        clearAccessSensitiveResults();
      }),
    [clearAccessSensitiveResults]
  );

  const previousViewerAccessContextKeyRef = useRef<string | null>(
    viewerAccessContextKey
  );

  useEffect(() => {
    const previousKey = previousViewerAccessContextKeyRef.current;
    if (previousKey === viewerAccessContextKey) return;

    previousViewerAccessContextKeyRef.current = viewerAccessContextKey;
    if (previousKey !== null) {
      clearAccessSensitiveResults();
    }
  }, [clearAccessSensitiveResults, viewerAccessContextKey]);

  useEffect(() => {
    const onAccessContextUpdated = () => clearAccessSensitiveResults();
    const onStorage = (event: StorageEvent) => {
      if (event.key === ACCESS_CONTEXT_UPDATED_AT_KEY) {
        clearAccessSensitiveResults();
      }
    };

    window.addEventListener(ACCESS_CONTEXT_UPDATED_EVENT, onAccessContextUpdated);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(ACCESS_CONTEXT_UPDATED_EVENT, onAccessContextUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, [clearAccessSensitiveResults]);

  // SearchArea から「検索実行」を受ける
  const handleSearchPerformed = useCallback((reason?: string) => {
    setSearchPerformed(true);
    // manual（ユーザー操作）のときだけ “ユーザー検索” 扱いにする
    const isUserSearch =
      reason === 'manual' || String(reason ?? '').startsWith('auto-search:url');

    if (!isUserSearch) {
      // 自動検索 / 復元 / revalidate はユーザー検索扱いにしない
      return;
    }

    // manual は即ユーザー検索として確定
    lastUserSearchAtRef.current = Date.now();
    keepResultsUntilUserSearchRef.current = false;
  }, []);


  // どこから results が空にされているかも追う
  const handleResults = useCallback(
    (next: Report[]) => {

      if (!Array.isArray(next)) return;
      if (!isViewerAccessContextReady) {
        clearAccessSensitiveResults();
        return;
      }

      // 非0件は常に採用（=表示して保持する）
      if (next.length > 0) {
        clearEmptyCommitTimer();
        updateMailExportReports(next);

        // ref を同期更新（ここが重要）
        resultsRef.current = next;
        hasNonEmptyRef.current = true;
        lastNonEmptyAtRef.current = Date.now();

        setResults(next);
        writeCache(next);

        return;
      }

      // ここから next.length === 0
      const now = Date.now();
      const hasNonEmpty = hasNonEmptyRef.current;
      const lastNonEmptyAt = lastNonEmptyAtRef.current;
      const lastUserSearchAt = lastUserSearchAtRef.current;
      // ルール1: 「戻り直後」かつ「すでに非0件を表示している」なら、空配列は無視
      if (keepResultsUntilUserSearchRef.current && hasNonEmpty) {
        return;
      }

      // ルール2: 非0件の直後に来る空配列（2→0）を無視
      //   これは SearchArea の多重 fetch / cleanup / race で起きがち
      const justReceivedNonEmpty = hasNonEmpty && now - lastNonEmptyAt < 1500;
      const userSearchRecent =
        lastUserSearchAt != null && now - lastUserSearchAt < 10000;

      if (justReceivedNonEmpty && !userSearchRecent) {
        return;
      }

      // ルール3: ユーザー検索の結果としての 0件は反映（ただしチラつき防止で少し待つ）
      if (userSearchRecent) {
        clearEmptyCommitTimer();
        emptyCommitTimerRef.current = window.setTimeout(() => {
          resultsRef.current = [];
          hasNonEmptyRef.current = false;
          lastNonEmptyAtRef.current = 0;

          setResults([]);
          updateMailExportReports([]);
          deleteCache();
        }, 300);
        return;
      }

      // それ以外は通常クリア
      clearEmptyCommitTimer();
      resultsRef.current = [];
      hasNonEmptyRef.current = false;
      lastNonEmptyAtRef.current = 0;

      setResults([]);
      updateMailExportReports([]);
      deleteCache();
    },
    [
      clearAccessSensitiveResults,
      clearEmptyCommitTimer,
      writeCache,
      deleteCache,
      isViewerAccessContextReady,
      updateMailExportReports,
    ]
  );

  return (
    <div>
      {/* <WebSocketDemo></WebSocketDemo> */}
      <Tabs />
      {/* 検索フォーム */}
      <SearchArea
        mailExportReports={mailExportReports}
        onResults={handleResults}
        onSearchPerformed={handleSearchPerformed}
      />
      {/* 検索結果表示 */}
      <Content
        results={isViewerAccessContextReady ? results : []}
        searchPerformed={searchPerformed}
        onMailExportReportsChange={updateMailExportReports}
      />
      {/* フッター */}
      <Footer />
    </div>
  );

};

export default WeeklyReport;
